import { Button, CheckboxGroup, DialogContent, DialogRoot, DialogTitle, DialogTrigger } from '@ibsys/kit';
import { Settings2Icon } from 'lucide-react';
import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSettings } from '@/entities/settings';
import { ConversationAttributesSettings } from '@/features/ConversationAttributesSettings';
import { cn } from '@/shared/lib';
import { DEFAULT_SAMPLE_COLUMNS, SAMPLE_COLUMN_KEYS, SAMPLE_COLUMNS } from './sampleColumns';

type Props = {
  className?: string;
};

export const SampleColumnsSettings: FC<Props> = ({ className }) => {
  const { t } = useTranslation(['samples', 'dialogs', 'common']);
  const { samplesPage: samplesSettings, setSamplesSettings } = useSettings();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [localMainColumns, setLocalMainColumns] = useState<string[]>([]);

  const visibleColumns = samplesSettings?.visibleColumns ?? DEFAULT_SAMPLE_COLUMNS;
  const mainColumnValues: string[] = Object.values(SAMPLE_COLUMNS);
  const selectedMainColumns = visibleColumns.filter((col) => mainColumnValues.includes(col));
  const selectedAttributePaths = visibleColumns.filter((col) => !mainColumnValues.includes(col));

  const mainColumnOptions = SAMPLE_COLUMN_KEYS.map((key) => ({
    value: SAMPLE_COLUMNS[key],
    label: t(`samples:column.${key}`),
  }));

  const handleDialogOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        // Initialize local state when dialog opens
        setLocalMainColumns(selectedMainColumns);
      }
      setIsDialogOpen(open);
    },
    [selectedMainColumns],
  );

  const handleMainColumnsChange = useCallback((columns: string[]) => {
    setLocalMainColumns(columns);
  }, []);

  const handleSave = useCallback(
    (attributePaths: string[]) => {
      // Combine main columns and attributes
      const allColumns = [...localMainColumns, ...attributePaths];
      setSamplesSettings({ visibleColumns: allColumns });
      setIsDialogOpen(false);
    },
    [localMainColumns, setSamplesSettings],
  );

  const handleCancel = useCallback(() => {
    setIsDialogOpen(false);
  }, []);

  const handleReset = useCallback(() => {
    setLocalMainColumns(selectedMainColumns);
  }, [selectedMainColumns]);

  return (
    <DialogRoot open={isDialogOpen} onOpenChange={handleDialogOpenChange}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          icon={<Settings2Icon />}
          className={cn(className, 'size-6')}
          aria-label={t('samples:columnsSettings')}
        />
      </DialogTrigger>
      <DialogContent className="max-w-160 h-180 overflow-auto flex flex-col">
        <DialogTitle>{t('samples:columnsSettings')}</DialogTitle>

        <div className="pb-4 border-b">
          <h5 className="font-roboto mb-2 text-sm text-muted-foreground">{t('samples:mainColumns')}</h5>
          <CheckboxGroup
            options={mainColumnOptions}
            name="sample-main-columns-settings"
            value={localMainColumns}
            onChange={handleMainColumnsChange}
          />
        </div>

        <h5 className="font-roboto mb-2 text-sm text-muted-foreground">{t('samples:attributes')}</h5>

        <ConversationAttributesSettings
          className="flex-1 flex flex-col"
          selectedPaths={selectedAttributePaths}
          onSave={handleSave}
          onCancel={handleCancel}
          onReset={handleReset}
        />
      </DialogContent>
    </DialogRoot>
  );
};
